import { useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { FileText, Lock, Paperclip, Trash2 } from 'lucide-react'
import {
  ETICHETE_DURATA_COLABORARE,
  ETICHETE_STARE_COLABORARE,
  ETICHETE_STARE_LUNA,
  ETICHETE_TIP_COLABORARE,
  TRANZITII_COLABORARE,
  lunaInCuvinteColaborare,
  type StareColaborare,
} from '@b4l/shared'
import { api } from '../../../lib/api'
import { PanouLateral, SectiunePanou } from '../../../components/ui/PanouLateral'
import { Pastila } from '../../../components/ui/Pastila'
import { Button } from '../../../components/ui/Button'
import { Modal } from '../../../components/ui/Modal'
import { Field } from '../../../components/ui/Field'
import { StareLista } from '../../../components/ui/StareLista'
import { useConfirmare } from '../../../components/ui/Confirmare'
import { FormularColaborare } from './FormularColaborare'
import {
  TON_LUNA,
  TON_STARE,
  dataRo,
  eroare,
  lei,
  type Colaborare,
  type Fisier,
  type LunaEcran,
} from './comun'

const camp = 'w-full rounded border border-b4l-border bg-b4l-surface px-3 py-2 text-sm'

async function deschide(colaborareId: number, docId: number) {
  try {
    const r = await api.get(`/colaborari/${colaborareId}/documente/${docId}`, {
      responseType: 'blob',
      timeout: 60_000,
    })
    window.open(URL.createObjectURL(r.data as Blob), '_blank', 'noopener')
  } catch {
    toast.error('Fișierul nu se poate deschide.')
  }
}

/** Fisa unei colaborari, in panoul din dreapta: datele, starea, contractul si lunile. */
export function PanouColaborare({
  id,
  poateScrie: poateScriePagina,
  scrieTot,
  eu,
  responsabili,
  onInchide,
}: {
  id: number
  poateScrie: boolean
  /** Adminul scrie si colaborarile altora; validatorul doar le vede. */
  scrieTot: boolean
  eu: number | undefined
  responsabili: { id: number; name: string }[]
  onInchide: () => void
}) {
  const qc = useQueryClient()
  const { confirma } = useConfirmare()
  const [modifica, setModifica] = useState(false)
  const [stareNoua, setStareNoua] = useState<StareColaborare | null>(null)
  const [lunaDeschisa, setLunaDeschisa] = useState<LunaEcran | null>(null)
  const fisier = useRef<HTMLInputElement>(null)

  const q = useQuery({
    queryKey: ['colaborari', 'fisa', id],
    queryFn: async () =>
      (await api.get<{ colaborare: Colaborare }>(`/colaborari/${id}`)).data.colaborare,
  })
  const c = q.data
  const poateScrie = poateScriePagina && !!c && (scrieTot || c.responsabil.id === eu)
  const reincarca = () => void qc.invalidateQueries({ queryKey: ['colaborari'] })

  const urca = useMutation({
    mutationFn: async (f: File) => {
      const fd = new FormData()
      fd.append('file', f)
      await api.post(`/colaborari/${id}/documente`, fd, { timeout: 120_000 })
    },
    onSuccess: () => {
      reincarca()
      toast.success('Contractul a fost atașat.')
    },
    onError: (e) => toast.error(eroare(e, 'Fișierul nu s-a încărcat.')),
  })

  const stergeDoc = useMutation({
    mutationFn: async (docId: number) => api.delete(`/colaborari/${id}/documente/${docId}`),
    onSuccess: reincarca,
    onError: (e) => toast.error(eroare(e)),
  })

  const sterge = useMutation({
    mutationFn: async () => api.delete(`/colaborari/${id}`),
    onSuccess: () => {
      reincarca()
      toast.success('Colaborarea a fost ștearsă.')
      onInchide()
    },
    onError: (e) => toast.error(eroare(e)),
  })

  return (
    <PanouLateral
      deschis
      onInchide={onInchide}
      titlu={c?.denumire ?? 'Colaborare'}
      subtitlu={
        c && (
          <>
            <Pastila ton={TON_STARE[c.stare]}>{ETICHETE_STARE_COLABORARE[c.stare]}</Pastila>
            <Pastila ton="gri">{ETICHETE_TIP_COLABORARE[c.tip]}</Pastila>
          </>
        )
      }
    >
      <StareLista interogare={q} gol={!c} mesajGol="Colaborarea nu mai există.">
        {c && (
          <>
            <SectiunePanou titlu="Contract">
              <dl className="grid grid-cols-[120px_1fr] gap-x-3 gap-y-1.5 text-sm">
                <dt className="text-b4l-text-muted">Responsabil</dt>
                <dd>{c.responsabil.name}</dd>
                <dt className="text-b4l-text-muted">Durată</dt>
                <dd>{ETICHETE_DURATA_COLABORARE[c.durata]}</dd>
                <dt className="text-b4l-text-muted">Semnat</dt>
                <dd>{dataRo(c.dataSemnare)}</dd>
                <dt className="text-b4l-text-muted">Perioada</dt>
                <dd>
                  {dataRo(c.valabilDe)} – {c.valabilPana ? dataRo(c.valabilPana) : 'nedeterminată'}
                </dd>
                {c.partener && (
                  <>
                    <dt className="text-b4l-text-muted">Partener CRM</dt>
                    <dd>{c.partener.denumire}</dd>
                  </>
                )}
                {(c.specialitate || c.unitate || c.localitate) && (
                  <>
                    <dt className="text-b4l-text-muted">Unde</dt>
                    <dd>{[c.specialitate, c.unitate, c.localitate].filter(Boolean).join(' · ')}</dd>
                  </>
                )}
                {c.contact && (
                  <>
                    <dt className="text-b4l-text-muted">Contact</dt>
                    <dd>{c.contact}</dd>
                  </>
                )}
                <dt className="text-b4l-text-muted">Obiect</dt>
                <dd className="whitespace-pre-line">{c.obiect}</dd>
                <dt className="text-b4l-text-muted">Valoare</dt>
                <dd>{lei(c.valoare)}</dd>
                {c.conditii && (
                  <>
                    <dt className="text-b4l-text-muted">Condiții</dt>
                    <dd className="whitespace-pre-line">{c.conditii}</dd>
                  </>
                )}
              </dl>
              {poateScrie && (
                <div className="mt-3 flex flex-wrap gap-2">
                  <Button
                    variant="secondary"
                    onClick={() => setModifica(true)}
                    className="!py-1 !text-xs"
                  >
                    Modifică
                  </Button>
                  {TRANZITII_COLABORARE[c.stare].map((s) => (
                    <Button
                      key={s}
                      variant="secondary"
                      onClick={() => setStareNoua(s)}
                      className="!py-1 !text-xs"
                    >
                      {s === 'activ'
                        ? c.stare === 'suspendat'
                          ? 'Reia'
                          : 'Activează'
                        : s === 'suspendat'
                          ? 'Suspendă'
                          : 'Încheie'}
                    </Button>
                  ))}
                  {c.luni.every((l) => !l.rand) && c.documente.length === 0 && (
                    <Button
                      variant="ghost"
                      className="!py-1 !text-xs text-b4l-red-dark"
                      onClick={async () => {
                        if (
                          await confirma({
                            titlu: 'Ștergi colaborarea?',
                            mesaj: 'Se șterge cu tot istoricul stărilor. Nu se poate reface.',
                            buton: 'Șterge',
                            pericol: true,
                          })
                        )
                          sterge.mutate()
                      }}
                    >
                      Șterge
                    </Button>
                  )}
                </div>
              )}
            </SectiunePanou>

            <SectiunePanou titlu="Contractul semnat">
              {c.documente.length === 0 && (
                <p className="text-sm text-b4l-text-muted">Niciun document atașat.</p>
              )}
              <ul className="grid gap-1">
                {c.documente.map((d) => (
                  <RandFisier
                    key={d.id}
                    f={d}
                    onDeschide={() => void deschide(c.id, d.id)}
                    onSterge={
                      poateScrie
                        ? async () => {
                            if (
                              await confirma({
                                titlu: `Ștergi ${d.numeFisier}?`,
                                buton: 'Șterge',
                                pericol: true,
                              })
                            )
                              stergeDoc.mutate(d.id)
                          }
                        : undefined
                    }
                  />
                ))}
              </ul>
              {poateScrie && (
                <>
                  <input
                    ref={fisier}
                    type="file"
                    className="hidden"
                    accept=".pdf,.doc,.docx,.xls,.xlsx,image/*"
                    onChange={(e) => {
                      const f = e.target.files?.[0]
                      if (f) urca.mutate(f)
                      e.target.value = ''
                    }}
                  />
                  <Button
                    variant="secondary"
                    className="mt-2 !py-1 !text-xs"
                    loading={urca.isPending}
                    onClick={() => fisier.current?.click()}
                  >
                    <Paperclip size={13} className="mr-1 inline" /> Atașează contractul (PDF)
                  </Button>
                </>
              )}
            </SectiunePanou>

            {c.durata === 'continua' && (
              <SectiunePanou titlu="Mentenanță lunară">
                {c.luni.length === 0 ? (
                  <p className="text-sm text-b4l-text-muted">
                    Lunile apar de când colaborarea e activă.
                  </p>
                ) : (
                  <ul className="grid gap-1.5">
                    {[...c.luni].reverse().map((l) => (
                      <li
                        key={l.luna}
                        className="rounded border border-b4l-border px-3 py-2 text-sm"
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-medium capitalize">
                            {lunaInCuvinteColaborare(l.luna)}
                            {l.validata && (
                              <Lock
                                size={12}
                                className="ml-1 inline text-b4l-text-muted"
                                aria-label="Lună validată"
                              />
                            )}
                          </span>
                          <span className="flex items-center gap-2">
                            <Pastila ton={TON_LUNA[l.stare]}>
                              {ETICHETE_STARE_LUNA[l.stare]}
                            </Pastila>
                            {poateScrie && !l.validata && (
                              <button
                                type="button"
                                className="text-xs text-b4l-blue-dark underline"
                                onClick={() => setLunaDeschisa(l)}
                              >
                                {l.rand ? 'Modifică' : 'Completează'}
                              </button>
                            )}
                          </span>
                        </div>
                        {l.rand?.descriere && (
                          <p className="mt-1 whitespace-pre-line text-b4l-text-muted">
                            {l.rand.dataActivitate && `${dataRo(l.rand.dataActivitate)} · `}
                            {l.rand.descriere}
                          </p>
                        )}
                        {l.rand && l.rand.dovezi.length > 0 && (
                          <ul className="mt-1 grid gap-1">
                            {l.rand.dovezi.map((d) => (
                              <RandFisier
                                key={d.id}
                                f={d}
                                onDeschide={() => void deschide(c.id, d.id)}
                                onSterge={
                                  poateScrie && !l.validata
                                    ? async () => {
                                        if (
                                          await confirma({
                                            titlu: `Ștergi ${d.numeFisier}?`,
                                            buton: 'Șterge',
                                            pericol: true,
                                          })
                                        )
                                          stergeDoc.mutate(d.id)
                                      }
                                    : undefined
                                }
                              />
                            ))}
                          </ul>
                        )}
                        {!l.ceruta && (
                          <p className="mt-1 text-xs text-b4l-text-dim">
                            Colaborarea n-a fost activă în luna asta.
                          </p>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </SectiunePanou>
            )}

            <SectiunePanou titlu="Istoric">
              <ul className="grid gap-1 text-sm">
                {c.istoric.map((s) => (
                  <li key={s.id}>
                    <span className="font-medium">{ETICHETE_STARE_COLABORARE[s.stare]}</span> de la{' '}
                    {dataRo(s.dela)}
                    <span className="text-b4l-text-muted"> · {s.de}</span>
                    {s.nota && <span className="text-b4l-text-muted"> — {s.nota}</span>}
                  </li>
                ))}
              </ul>
            </SectiunePanou>

            {modifica && (
              <FormularColaborare
                initial={c}
                responsabili={responsabili}
                onInchide={() => setModifica(false)}
              />
            )}
            {stareNoua && (
              <FereastraStare c={c} spre={stareNoua} onInchide={() => setStareNoua(null)} />
            )}
            {lunaDeschisa && (
              <FereastraLuna c={c} l={lunaDeschisa} onInchide={() => setLunaDeschisa(null)} />
            )}
          </>
        )}
      </StareLista>
    </PanouLateral>
  )
}

function RandFisier({
  f,
  onDeschide,
  onSterge,
}: {
  f: Fisier
  onDeschide: () => void
  onSterge?: () => void
}) {
  return (
    <li className="flex items-center justify-between gap-2 text-sm">
      <button
        type="button"
        className="flex min-w-0 items-center gap-1.5 text-b4l-blue-dark hover:underline"
        onClick={onDeschide}
      >
        <FileText size={14} className="shrink-0" />
        <span className="truncate">{f.numeFisier}</span>
      </button>
      {onSterge && (
        <button
          type="button"
          aria-label={`Șterge ${f.numeFisier}`}
          onClick={onSterge}
          className="text-b4l-text-muted hover:text-b4l-red-dark"
        >
          <Trash2 size={14} />
        </button>
      )}
    </li>
  )
}

function FereastraStare({
  c,
  spre,
  onInchide,
}: {
  c: Colaborare
  spre: StareColaborare
  onInchide: () => void
}) {
  const qc = useQueryClient()
  // Prima activare: implicit de la semnare (sau de la inceputul perioadei, daca e dupa) — asa se
  // cer si lunile dinainte de ziua in care s-a trecut in CRM. Altfel: azi.
  const [dela, setDela] = useState(() =>
    spre === 'activ' && c.stare === 'negociere' && c.dataSemnare
      ? c.dataSemnare > c.valabilDe
        ? c.dataSemnare
        : c.valabilDe
      : new Date().toLocaleDateString('en-CA', { timeZone: 'Europe/Bucharest' }),
  )
  const [nota, setNota] = useState('')
  const m = useMutation({
    mutationFn: async () => api.post(`/colaborari/${c.id}/stare`, { stare: spre, dela, nota }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['colaborari'] })
      toast.success(`Colaborarea e acum „${ETICHETE_STARE_COLABORARE[spre].toLowerCase()}".`)
      onInchide()
    },
    onError: (e) => toast.error(eroare(e)),
  })
  return (
    <Modal
      open
      onClose={onInchide}
      title={`${ETICHETE_STARE_COLABORARE[spre]} — ${c.denumire}`}
      footer={
        <>
          <Button variant="secondary" onClick={onInchide}>
            Renunță
          </Button>
          <Button onClick={() => m.mutate()} loading={m.isPending}>
            Confirmă
          </Button>
        </>
      }
    >
      <div className="grid gap-3">
        <Field
          label="De la data"
          type="date"
          value={dela}
          onChange={(e) => setDela(e.target.value)}
          hint="Lunile de mentenanță se socotesc de la data asta."
        />
        {spre === 'activ' && !c.dataSemnare && (
          <p className="text-sm text-b4l-red-dark">
            Trece întâi data semnării (Modifică), apoi activează colaborarea.
          </p>
        )}
        <Field label="Notă (opțional)" value={nota} onChange={(e) => setNota(e.target.value)} />
      </div>
    </Modal>
  )
}

function FereastraLuna({
  c,
  l,
  onInchide,
}: {
  c: Colaborare
  l: LunaEcran
  onInchide: () => void
}) {
  const qc = useQueryClient()
  const [are, setAre] = useState(l.rand?.areActivitate ?? true)
  const [descriere, setDescriere] = useState(l.rand?.descriere ?? '')
  const [data, setData] = useState(l.rand?.dataActivitate ?? '')
  const [dovezi, setDovezi] = useState<File[]>([])

  const m = useMutation({
    mutationFn: async () => {
      await api.put(`/colaborari/${c.id}/luni/${l.luna}`, {
        areActivitate: are,
        descriere,
        dataActivitate: are ? data : '',
      })
      const cazute: string[] = []
      if (are)
        for (const f of dovezi) {
          const fd = new FormData()
          fd.append('luna', l.luna)
          fd.append('file', f)
          try {
            await api.post(`/colaborari/${c.id}/documente`, fd, { timeout: 120_000 })
          } catch (e) {
            cazute.push(`${f.name}: ${eroare(e, 'nu s-a încărcat')}`)
          }
        }
      return cazute
    },
    onSuccess: (cazute) => {
      void qc.invalidateQueries({ queryKey: ['colaborari'] })
      if (cazute.length) toast.error(cazute.join(' · '), { duration: 12000 })
      else toast.success('Luna a fost salvată.')
      onInchide()
    },
    onError: (e) => toast.error(eroare(e)),
  })

  const goleste = useMutation({
    mutationFn: async () => api.delete(`/colaborari/${c.id}/luni/${l.luna}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['colaborari'] })
      onInchide()
    },
    onError: (e) => toast.error(eroare(e)),
  })

  return (
    <Modal
      open
      onClose={onInchide}
      title={`${c.denumire} — ${lunaInCuvinteColaborare(l.luna)}`}
      footer={
        <>
          {l.rand && (
            <Button variant="ghost" onClick={() => goleste.mutate()} loading={goleste.isPending}>
              Golește luna
            </Button>
          )}
          <Button variant="secondary" onClick={onInchide}>
            Renunță
          </Button>
          <Button onClick={() => m.mutate()} loading={m.isPending}>
            Salvează
          </Button>
        </>
      }
    >
      <div className="grid gap-3">
        <div className="flex gap-4 text-sm">
          <label className="flex items-center gap-2">
            <input type="radio" checked={are} onChange={() => setAre(true)} /> Cu activitate
          </label>
          <label className="flex items-center gap-2">
            <input type="radio" checked={!are} onChange={() => setAre(false)} /> Fără activitate
          </label>
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-b4l-text-muted">
            {are ? 'Ce s-a făcut' : 'Notă (opțional)'}
          </label>
          <textarea
            value={descriere}
            onChange={(e) => setDescriere(e.target.value)}
            rows={4}
            className={camp}
            placeholder={
              are ? 'Prezentare la clinică, 30 de pacienți; materiale lăsate la recepție…' : ''
            }
          />
        </div>
        {are && (
          <>
            <Field
              label="Data activității (opțional)"
              type="date"
              value={data}
              min={`${l.luna}-01`}
              max={`${l.luna}-31`}
              onChange={(e) => setData(e.target.value)}
            />
            <div>
              <label className="mb-1 block text-xs font-medium text-b4l-text-muted">
                Dovezi (poze, PDF) — opțional
              </label>
              <input
                type="file"
                multiple
                accept=".pdf,.doc,.docx,.xls,.xlsx,image/*"
                onChange={(e) => setDovezi([...(e.target.files ?? [])])}
                className="text-sm"
              />
              {l.rand && l.rand.dovezi.length > 0 && (
                <p className="mt-1 text-xs text-b4l-text-muted">
                  Deja atașate: {l.rand.dovezi.map((d) => d.numeFisier).join(', ')}
                </p>
              )}
            </div>
          </>
        )}
      </div>
    </Modal>
  )
}
